import TasksContent from '@/task/components/TasksContent'
import TasksHeader from '@/task/components/TasksHeader'
import { TasksModuleProvider } from '@/task/contexts/TasksModuleContext'
import { Box, Button, ButtonGroup } from '@chakra-ui/react'
import { Task_Status_Enum, useAppendMeetingStepDataMutation } from '@gql'
import { LogType } from '@rolebase/shared/model/log'
import {
  MeetingStepDataTasks,
  MeetingStepTasksFragment,
} from '@rolebase/shared/model/meeting_step'
import {
  RoleReviewLabelKey,
  RoleReviewMode,
  RoleReviewTextKey,
} from '@rolebase/shared/model/roleReview'
import { TasksViewTypes } from '@rolebase/shared/model/task'
import throttle from 'lodash.throttle'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { MeetingContext } from '../contexts/MeetingContext'
import MeetingLogs from './MeetingLogs'
import MeetingRoleReview from './MeetingRoleReview'
import { getTasksStepModule } from './meetingStepTasksModule'

interface Props {
  step: MeetingStepTasksFragment
}

// Tasks are no longer logged (the log system covers the org chart only).
export const taskLogTypes: LogType[] = []

const reviewModes: RoleReviewMode[] = ['Projects', 'Texts']

// A partial write of the step's data: settings and review texts alike
export type TasksDataPatch = Partial<
  Omit<MeetingStepDataTasks, RoleReviewTextKey | RoleReviewLabelKey>
> & { [key: RoleReviewTextKey]: string } & {
  [key: RoleReviewLabelKey]: string
}

export default function MeetingStepContentTasks({ step }: Props) {
  const { t } = useTranslation()
  const { meeting, circle, isEnded } = useContext(MeetingContext)!
  const { orgData } = useOrgContext()

  // The circles whose projects this step lists: the circle, then its direct
  // children — the perimeter the Checklist and Indicators steps already use
  // (REU12-D1). Without it the step showed only what the circle carried
  // itself, never what its roles or sub-circles did (REU-12).
  const circleIds = useMemo(
    () =>
      circle && orgData
        ? orgData.circleWithChildren(circle.id).map((c) => c.id)
        : undefined,
    [circle, orgData]
  )

  // Cached data for optimistic updates
  const [cachedData, setCachedData] = useState<
    MeetingStepTasksFragment['data']
  >(step.data)
  useEffect(() => setCachedData(step.data), [step.data])

  // Only what changed is sent, merged into the stored data by `_append` (#80).
  // Writing the whole object back, as this step used to, would erase a review
  // text another participant saved meanwhile. Changes made in the same instant
  // (a filter and its reset) are gathered and sent together.
  const [appendMeetingStepData] = useAppendMeetingStepDataMutation()
  const pendingRef = useRef<TasksDataPatch>({})
  const flushThrottled = useMemo(
    () =>
      throttle(
        () => {
          const data = pendingRef.current
          pendingRef.current = {}
          appendMeetingStepData({ variables: { id: step.id, data } })
        },
        100,
        { leading: false }
      ),
    [step.id, appendMeetingStepData]
  )

  const updateData = useCallback(
    (data: TasksDataPatch) => {
      pendingRef.current = { ...pendingRef.current, ...data }
      setCachedData(
        (prev) => ({ ...prev, ...data }) as MeetingStepTasksFragment['data']
      )
      flushThrottled()
    },
    [flushThrottled]
  )

  const handleViewChange = useCallback(
    (viewType: TasksViewTypes) => updateData({ viewType }),
    [updateData]
  )

  const handleMemberChange = useCallback(
    (memberId: string | undefined) =>
      updateData({ filterMemberId: memberId || null }),
    [updateData]
  )

  const handleStatusChange = useCallback(
    (status: Task_Status_Enum | undefined) =>
      updateData({ filterStatus: status || null }),
    [updateData]
  )

  if (!meeting || !circle) return null
  const reviewMode = cachedData.reviewMode ?? 'Projects'
  const tasksModule = getTasksStepModule(cachedData, isEnded, {
    onViewChange: handleViewChange,
    onMemberChange: handleMemberChange,
    onStatusChange: handleStatusChange,
  })

  return (
    <Box my={5}>
      {/* Review by projects (default) or by texts (#80). Once the meeting has
          ended, the choice stays as it was. */}
      <ButtonGroup size="sm" isAttached variant="outline" mb={4}>
        {reviewModes.map((mode) => (
          <Button
            key={mode}
            isActive={mode === reviewMode}
            isDisabled={isEnded}
            onClick={() =>
              mode !== reviewMode && updateData({ reviewMode: mode })
            }
          >
            {t(`MeetingRoleReview.mode${mode}`)}
          </Button>
        ))}
      </ButtonGroup>

      {reviewMode === 'Texts' && (
        <MeetingRoleReview
          stepId={step.id}
          circleId={circle.id}
          data={cachedData}
          readOnly={isEnded}
          onChange={updateData}
        />
      )}

      {tasksModule && (
        <TasksModuleProvider
          {...tasksModule}
          // circleId stays the meeting's circle: it is where a new project
          // lands (REU12-D4). circleIds is only what the step reads.
          circleId={circle.id}
          circleIds={circleIds}
        >
          <TasksHeader pb={5} />
          <TasksContent
            overflowContainer={{
              expandLeft: true,
              expandRight: true,
            }}
          />
        </TasksModuleProvider>
      )}

      <MeetingLogs
        meetingId={meeting.id}
        title={t('MeetingStepContentTasks.logs')}
        includeTypes={taskLogTypes}
        hideEmpty
        mt={10}
      />
    </Box>
  )
}
